import type { ApiResponse } from '@bcube/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AccountAddress, AccountContact, AccountContactItem } from '@/api/commercial/accounts';
import { useAuth } from '@/context/auth-context';
import { api } from '@/lib/api';

// Extended types with local IDs
export interface LocalAccountDocument {
  value: string;
  type: 'cpf' | 'cnpj';
}

export interface LocalAccountSituationIE {
  id?: string;
  status?: boolean;
  text?: string;
}

export interface LocalAccount {
  id: string;
  document: LocalAccountDocument;
  name?: string;
  rg?: string;
  social_name?: string;
  fantasy_name?: string;
  state_registration?: string;
  municipal_registration?: string;
  status?: string;
  situationIE?: LocalAccountSituationIE;
  typeIE?: string;
  setor?: string[];
  accountType?: 'MEI' | 'ME' | 'EPP' | 'DEMAIS' | 'PF';
  simeiSince?: string;
  natureText?: string;
  economicGroupId?: string;
  cnpjaRegistrations?: Array<{
    number: string;
    state?: string;
    statusDate?: string;
    enabled: boolean;
    status: { id?: number; text?: string } | null;
    type: { id?: number; text?: string } | null;
  }>;
}

export interface LocalAddress extends Omit<AccountAddress, 'accountId'> {
  accountId?: string;
}

export type LocalContactItem = AccountContactItem;

export interface LocalContact extends Omit<AccountContact, 'accountId'> {
  accountId?: string;
}

export interface LocalAccountEconomicGroup {
  existingGroupId?: string;
  economic_group_holding?: {
    name: string;
    taxId: string;
  };
  economic_group_controlled?: Array<{
    name: string;
    taxId: string;
  }>;
  autoDetected?: boolean;
}

const STORAGE_KEYS = {
  ACCOUNT: 'create_account_flow_account',
  ADDRESSES: 'create_account_flow_addresses',
  CONTACTS: 'create_account_flow_contacts',
  ECONOMIC_GROUP: 'create_account_flow_economic_group',
} as const;

function getFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;

  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to parse ${key} from localStorage:`, error);
    return defaultValue;
  }
}

function setToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
}

function removeFromStorage(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove ${key} from localStorage:`, error);
  }
}

interface CreateAccountFlowContextType {
  // Account state
  account: LocalAccount | null;

  // Address state
  addresses: LocalAddress[];

  // Contact state
  contacts: LocalContact[];

  // Economic group state
  economicGroup: LocalAccountEconomicGroup | null;

  // Account methods
  createAccountLocally: (account: LocalAccount) => void;
  updateAccountLocally: (updates: Partial<LocalAccount>) => void;
  deleteAccountLocally: () => void;
  setAccount: (account: LocalAccount | null) => void;

  // Address methods
  createAddressLocally: (address: LocalAddress) => void;
  updateAddressLocally: (id: string, updates: Partial<LocalAddress>) => void;
  deleteAddressLocally: (id: string) => void;
  setAddresses: (addresses: LocalAddress[]) => void;

  // Contact methods
  createContactLocally: (contact: LocalContact) => void;
  updateContactLocally: (id: string, updates: Partial<LocalContact>) => void;
  deleteContactLocally: (id: string) => void;
  setContacts: (contacts: LocalContact[]) => void;

  // Economic group methods
  createEconomicGroupLocally: (economicGroup: LocalAccountEconomicGroup) => void;
  updateEconomicGroupLocally: (updates: Partial<LocalAccountEconomicGroup>) => void;
  deleteEconomicGroupLocally: () => void;
  setEconomicGroup: (economicGroup: LocalAccountEconomicGroup | null) => void;

  // Utility methods
  resetFlow: () => void;

  // API creation method
  createEntitiesApi: () => Promise<{
    success: boolean;
    accountId?: string;
    error?: string;
  }>;
}

const CreateAccountFlowContext = createContext<CreateAccountFlowContextType>(
  {} as CreateAccountFlowContextType,
);

interface CreateAccountFlowProviderProps {
  children: ReactNode;
  resetOnMount?: boolean;
}

export function CreateAccountFlowProvider({
  children,
  resetOnMount = false,
}: CreateAccountFlowProviderProps) {
  const { user } = useAuth();

  // State to hold the current localStorage data
  const [currentData, setCurrentData] = useState({
    account: null as LocalAccount | null,
    addresses: [] as LocalAddress[],
    contacts: [] as LocalContact[],
    economicGroup: null as LocalAccountEconomicGroup | null,
  });

  // Function to load data from localStorage
  const loadDataFromLocalStorage = useCallback(() => {
    const newData = {
      account: getFromStorage<LocalAccount | null>(STORAGE_KEYS.ACCOUNT, null),
      addresses: getFromStorage<LocalAddress[]>(STORAGE_KEYS.ADDRESSES, []),
      contacts: getFromStorage<LocalContact[]>(STORAGE_KEYS.CONTACTS, []),
      economicGroup: getFromStorage<LocalAccountEconomicGroup | null>(
        STORAGE_KEYS.ECONOMIC_GROUP,
        null,
      ),
    };

    setCurrentData(newData);
  }, []);

  // Load data on mount and when localStorage changes
  useEffect(() => {
    loadDataFromLocalStorage();
  }, [loadDataFromLocalStorage]);

  // Listen for localStorage changes and reload data
  useEffect(() => {
    const handleStorageChange = () => {
      loadDataFromLocalStorage();
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadDataFromLocalStorage]);

  // Getter functions that read and JSON parse directly from localStorage
  const getAccount = useCallback((): LocalAccount | null => {
    return currentData.account;
  }, [currentData.account]);

  const getAddresses = useCallback((): LocalAddress[] => {
    const addresses = getFromStorage<LocalAddress[]>(STORAGE_KEYS.ADDRESSES, []);
    return Array.isArray(addresses) ? addresses : [];
  }, []);

  const getContacts = useCallback((): LocalContact[] => {
    const contacts = getFromStorage<LocalContact[]>(STORAGE_KEYS.CONTACTS, []);
    return Array.isArray(contacts) ? contacts : [];
  }, []);

  const getEconomicGroup = useCallback((): LocalAccountEconomicGroup | null => {
    const economicGroup = getFromStorage<LocalAccountEconomicGroup | null>(
      STORAGE_KEYS.ECONOMIC_GROUP,
      null,
    );
    return economicGroup && typeof economicGroup === 'object' ? economicGroup : null;
  }, []);

  // Setter functions that write and JSON stringify directly to localStorage
  const setAccount = useCallback(
    (newAccount: LocalAccount | null) => {
      if (newAccount && typeof newAccount === 'object') {
        setToStorage<LocalAccount>(STORAGE_KEYS.ACCOUNT, newAccount);
      } else {
        removeFromStorage(STORAGE_KEYS.ACCOUNT);
      }

      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage],
  );

  const setAddresses = useCallback(
    (newAddresses: LocalAddress[]) => {
      if (Array.isArray(newAddresses)) {
        setToStorage<LocalAddress[]>(STORAGE_KEYS.ADDRESSES, newAddresses);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage],
  );

  const setContacts = useCallback(
    (newContacts: LocalContact[]) => {
      if (Array.isArray(newContacts)) {
        setToStorage<LocalContact[]>(STORAGE_KEYS.CONTACTS, newContacts);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage],
  );

  const setEconomicGroup = useCallback(
    (newEconomicGroup: LocalAccountEconomicGroup | null) => {
      if (newEconomicGroup && typeof newEconomicGroup === 'object') {
        setToStorage<LocalAccountEconomicGroup>(STORAGE_KEYS.ECONOMIC_GROUP, newEconomicGroup);
      } else {
        removeFromStorage(STORAGE_KEYS.ECONOMIC_GROUP);
      }
      loadDataFromLocalStorage();
    },
    [loadDataFromLocalStorage],
  );

  // Account methods
  const createAccountLocally = useCallback(
    (newAccount: LocalAccount) => {
      setAccount(newAccount);
    },
    [setAccount],
  );

  const updateAccountLocally = useCallback(
    (updates: Partial<LocalAccount>) => {
      const currentAccount = getAccount();
      if (currentAccount) {
        const updatedAccount = { ...currentAccount, ...updates };
        setAccount(updatedAccount);
      }
    },
    [getAccount, setAccount],
  );

  const deleteAccountLocally = useCallback(() => {
    setAccount(null);
    // Clear related data when account is deleted
    setAddresses([]);
    setContacts([]);
    setEconomicGroup(null);
  }, [setAccount, setAddresses, setContacts, setEconomicGroup]);

  // Address methods
  const createAddressLocally = useCallback(
    (newAddress: LocalAddress) => {
      const currentAddresses = getAddresses();
      const updatedAddresses = [...currentAddresses, newAddress];
      setAddresses(updatedAddresses);
    },
    [getAddresses, setAddresses],
  );

  const updateAddressLocally = useCallback(
    (id: string, updates: Partial<LocalAddress>) => {
      const currentAddresses = getAddresses();
      const updatedAddresses = currentAddresses.map((address) => {
        return address.id === id ? { ...address, ...updates } : address;
      });
      setAddresses(updatedAddresses);
    },
    [getAddresses, setAddresses],
  );

  const deleteAddressLocally = useCallback(
    (id: string) => {
      const currentAddresses = getAddresses();
      const filteredAddresses = currentAddresses.filter((address) => address.id !== id);
      setAddresses(filteredAddresses);
    },
    [getAddresses, setAddresses],
  );

  // Economic group methods
  const createEconomicGroupLocally = useCallback(
    (newEconomicGroup: LocalAccountEconomicGroup) => {
      setEconomicGroup(newEconomicGroup);
    },
    [setEconomicGroup],
  );

  const updateEconomicGroupLocally = useCallback(
    (updates: Partial<LocalAccountEconomicGroup>) => {
      const currentEconomicGroup = getEconomicGroup();
      if (currentEconomicGroup) {
        const updatedEconomicGroup = { ...currentEconomicGroup, ...updates };
        setEconomicGroup(updatedEconomicGroup);
      }
    },
    [getEconomicGroup, setEconomicGroup],
  );

  const deleteEconomicGroupLocally = useCallback(() => {
    setEconomicGroup(null);
  }, [setEconomicGroup]);

  // Contact methods
  const createContactLocally = useCallback(
    (newContact: LocalContact) => {
      const currentContacts = getContacts();
      const updatedContacts = [...currentContacts, newContact];
      setContacts(updatedContacts);
    },
    [getContacts, setContacts],
  );

  const updateContactLocally = useCallback(
    (id: string, updates: Partial<LocalContact>) => {
      const currentContacts = getContacts();
      const updatedContacts = currentContacts.map((contact) =>
        contact.id === id ? { ...contact, ...updates } : contact,
      );
      setContacts(updatedContacts);
    },
    [getContacts, setContacts],
  );

  const deleteContactLocally = useCallback(
    (id: string) => {
      const currentContacts = getContacts();
      const filteredContacts = currentContacts.filter((contact) => contact.id !== id);
      setContacts(filteredContacts);
    },
    [getContacts, setContacts],
  );

  // Utility methods
  const resetFlow = useCallback(() => {
    setAccount(null);
    setAddresses([]);
    setContacts([]);
    setEconomicGroup(null);

    // Clear localStorage data
    removeFromStorage(STORAGE_KEYS.ACCOUNT);
    removeFromStorage(STORAGE_KEYS.ADDRESSES);
    removeFromStorage(STORAGE_KEYS.CONTACTS);
    removeFromStorage(STORAGE_KEYS.ECONOMIC_GROUP);
  }, [setAccount, setAddresses, setContacts, setEconomicGroup]);

  // Reset flow on mount if requested (for "Nova Conta" button clicks)
  useEffect(() => {
    if (resetOnMount) {
      resetFlow();
    }
  }, [resetOnMount, resetFlow]);

  // API creation method
  const createEntitiesApi = useCallback(async () => {
    try {
      const account = getAccount();
      const addresses = getAddresses();
      const contacts = getContacts();
      const economicGroup = getEconomicGroup();

      // Ensure account has required fields for API
      if (!account?.document?.value) {
        console.error('Account missing required document field');
        return {
          success: false,
          error: 'Dados da conta incompletos - documento obrigatório',
        };
      }

      let economicGroupId: string | undefined;
      let economicGroupWasUpdated = false;

      if (economicGroup) {
        try {
          if (economicGroup.autoDetected && economicGroup.existingGroupId) {
            // Account already belongs to a group (auto-detected) — just reference it
            economicGroupId = economicGroup.existingGroupId;
            economicGroupWasUpdated = false;
          } else if (economicGroup.existingGroupId) {
            // User selected an existing group to vinculate to — add as controlada
            const accountName = account.social_name || account.fantasy_name || account.name || '';
            await api.patch(
              `/commercial/economic-groups/${economicGroup.existingGroupId}/add-controlada`,
              { name: accountName, taxId: account.document.value },
            );
            economicGroupId = economicGroup.existingGroupId;
            economicGroupWasUpdated = true;
          } else {
            // Legacy flow: create or update economic group directly
            let existingEconomicGroup: { id: string } | null = null;

            if (economicGroup.economic_group_holding?.taxId) {
              const { data } = await api.get<ApiResponse<{ id: string } | null>>(
                '/commercial/economic-groups',
                {
                  params: {
                    'economic_group_holding.taxId': economicGroup.economic_group_holding.taxId,
                  },
                },
              );
              existingEconomicGroup = data.data;
            }

            if (existingEconomicGroup?.id) {
              const updateData = {
                economic_group_holding: economicGroup.economic_group_holding,
                economic_group_controlled: economicGroup.economic_group_controlled,
              };

              const { data: economicGroupResult } = await api.put<ApiResponse<{ id: string }>>(
                `/commercial/economic-groups/${existingEconomicGroup.id}`,
                updateData,
              );

              if (economicGroupResult?.data?.id) {
                economicGroupId = economicGroupResult.data.id;
                economicGroupWasUpdated = true;
              }
            } else {
              const economicGroupData = {
                economic_group_holding: economicGroup.economic_group_holding,
                economic_group_controlled: economicGroup.economic_group_controlled,
              };

              const { data: economicGroupResult } = await api.post<ApiResponse<{ id: string }>>(
                '/commercial/economic-groups',
                economicGroupData,
              );

              if (economicGroupResult?.data?.id) {
                economicGroupId = economicGroupResult.data.id;
                economicGroupWasUpdated = false;
              }
            }
          }
        } catch (error) {
          console.warn('Failed to create/update economic group:', error);
        }
      }

      // 2. Create the account with economic group ID
      const accountData = {
        ...account,
        economicGroupId,
      };

      const { data: accountResult } = await api.post<ApiResponse<{ id: string }>>(
        '/commercial/accounts',
        accountData,
      );

      if (!accountResult?.data?.id) {
        return {
          success: false,
          error: 'Erro ao criar conta',
        };
      }

      const createdAccountId = accountResult.data.id;

      // Create historical entry for account creation
      try {
        await api.post('/commercial/historicals', {
          accountId: createdAccountId,
          title: 'Cadastro da conta.',
          type: 'manual',
          author: {
            name: user?.name ?? '',
            avatarUrl: '',
          },
        });
      } catch (error) {
        console.warn('Failed to create historical entry for account:', error);
      }

      // 3. Create addresses with the account ID
      for (const address of addresses) {
        try {
          const addressData = {
            ...address,
            accountId: createdAccountId,
          };
          await api.post('/commercial/addresses', addressData);

          // Create historical entry for address creation
          try {
            await api.post('/commercial/historicals', {
              accountId: createdAccountId,
              title: `Endereço ${address.type?.join(', ') || 'comercial'} adicionado.`,
              type: 'manual',
              author: {
                name: user?.name ?? '',
                avatarUrl: '',
              },
            });
          } catch (error) {
            console.warn('Failed to create historical entry for address:', error);
          }
        } catch (error) {
          console.warn('Failed to create address:', error);
        }
      }

      // 4. Create contacts with the account ID
      for (const contact of contacts) {
        try {
          const contactData = {
            ...contact,
            accountId: createdAccountId,
          };
          await api.post('/commercial/contacts', contactData);

          // Create historical entry for contact creation
          try {
            await api.post('/commercial/historicals', {
              accountId: createdAccountId,
              title: `Contato "${contact.name}" adicionado.`,
              type: 'manual',
              author: {
                name: user?.name ?? '',
                avatarUrl: '',
              },
            });
          } catch (error) {
            console.warn('Failed to create historical entry for contact:', error);
          }
        } catch (error) {
          console.warn('Failed to create contact:', error);
        }
      }

      // 5. Create historical entries for economic groups if they exist
      if (economicGroup) {
        if (economicGroup.economic_group_holding) {
          try {
            const actionType = economicGroupWasUpdated ? 'atualizado' : 'vinculado';
            await api.post('/commercial/historicals', {
              accountId: createdAccountId,
              title: `Grupo econômico (Holding) "${economicGroup.economic_group_holding.name}" ${actionType}.`,
              type: 'manual',
              author: {
                name: user?.name ?? '',
                avatarUrl: '',
              },
            });
          } catch (error) {
            console.warn('Failed to create holding historical entry:', error);
          }
        }

        if (
          economicGroup.economic_group_controlled &&
          economicGroup.economic_group_controlled.length > 0
        ) {
          try {
            const controlledCount = economicGroup.economic_group_controlled.length;
            const isPlural = controlledCount > 1;
            const pluralSuffix = isPlural ? 's' : '';
            const controlledNames = economicGroup.economic_group_controlled
              .map((company) => company.name)
              .join(', ');

            const actionType = economicGroupWasUpdated ? 'atualizada' : 'vinculada';
            const actionTypePlural = economicGroupWasUpdated ? 'atualizadas' : 'vinculadas';
            const finalActionType = isPlural ? actionTypePlural : actionType;

            await api.post('/commercial/historicals', {
              accountId: createdAccountId,
              title: `Empresa${pluralSuffix} controlada${pluralSuffix} "${controlledNames}" ${finalActionType}.`,
              type: 'manual',
              author: {
                name: user?.name ?? '',
                avatarUrl: '',
              },
            });
          } catch (error) {
            console.warn('Failed to create controlled companies historical entry:', error);
          }
        }
      }

      return {
        success: true,
        accountId: createdAccountId,
      };
    } catch (error) {
      console.error('Error creating entities:', error);
      return {
        success: false,
        error: 'Erro inesperado ao criar entidades',
      };
    }
  }, [getAccount, getAddresses, getContacts, getEconomicGroup, user?.name]);

  // Memoize context value and trigger update when localStorage changes
  const value: CreateAccountFlowContextType = useMemo(
    () => ({
      // State - use current data from state
      account: currentData.account,
      addresses: currentData.addresses,
      contacts: currentData.contacts,
      economicGroup: currentData.economicGroup,

      // Account methods
      createAccountLocally,
      updateAccountLocally,
      deleteAccountLocally,
      setAccount,

      // Address methods
      createAddressLocally,
      updateAddressLocally,
      deleteAddressLocally,
      setAddresses,

      // Contact methods
      createContactLocally,
      updateContactLocally,
      deleteContactLocally,
      setContacts,

      // Economic group methods
      createEconomicGroupLocally,
      updateEconomicGroupLocally,
      deleteEconomicGroupLocally,
      setEconomicGroup,

      // Utility methods
      resetFlow,

      // API creation method
      createEntitiesApi,
    }),
    [
      currentData,
      createAccountLocally,
      updateAccountLocally,
      deleteAccountLocally,
      setAccount,
      createAddressLocally,
      updateAddressLocally,
      deleteAddressLocally,
      setAddresses,
      createContactLocally,
      updateContactLocally,
      deleteContactLocally,
      setContacts,
      createEconomicGroupLocally,
      updateEconomicGroupLocally,
      deleteEconomicGroupLocally,
      setEconomicGroup,
      resetFlow,
      createEntitiesApi,
    ],
  );

  return (
    <CreateAccountFlowContext.Provider value={value}>{children}</CreateAccountFlowContext.Provider>
  );
}

export function useCreateAccountFlow() {
  const context = useContext(CreateAccountFlowContext);
  if (!context) {
    throw new Error('useCreateAccountFlow must be used within a CreateAccountFlowProvider');
  }
  return context;
}
